import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Users, Briefcase, Award } from 'lucide-react';
import { gsap, useRevealOnScroll } from '../../hooks/useGsap';

/* ------------------------------------------------------------------
 * BACKGROUND DA SEÇÃO — ajuste fino aqui
 * ------------------------------------------------------------------
 * 1. A foto fica em public/media/img/ — caminho na constante BG_SRC.
 *    Crop ideal: panorâmico (~21:9), a turma enquadrada, sem texto grande.
 * 2. Enquanto o arquivo não existir, a seção volta sozinha ao
 *    fundo escuro original — nada quebra.
 * 3. Mexa nas constantes abaixo para calibrar o efeito.
 * ------------------------------------------------------------------ */
const BG_SRC = '/media/img/metrics-bg3.png';
const BG_OPACITY = 0.8;    // presença da foto (0.6 discreta | 1 cheia)
const BG_BLUR = '2px';     // desfoque forte: vira campo de cor, não concorre
const BG_BRIGHTNESS = 1.2; // > 1 clareia — a foto original é escura
const BG_SATURATE = 1.15;  // realça o verde da bandeira (0 = preto e branco)
const BG_ZOOM = 1.7;       // zoom: enquadra a turma e tira a bandeira gigante
const BG_ANCHOR = '14%';   // ponto fixo do zoom — menor = sobe para os rostos
const BG_SHIFT = '-20%';   // sobe/desce a foto: negativo sobe, positivo desce
const PARALLAX = 10;       // % de deslocamento no scroll (0 desliga)

const MetricCard = ({ icon: Icon, value, label, suffix = '', index }) => {
  const numRef = useRef(null);

  useEffect(() => {
    const el = numRef.current;
    if (!el) return;
    const target = parseInt(value, 10);
    const obj = { v: 0 };
    const ctx = gsap.context(() => {
      gsap.to(obj, {
        v: target,
        duration: 2,
        ease: 'power2.out',
        onUpdate: () => {
          el.textContent = Math.floor(obj.v).toString();
        },
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });
    });
    return () => ctx.revert();
  }, [value]);

  return (
    <div className="reveal group relative rounded-3xl border border-white/[0.14] bg-night-950/60 backdrop-blur-2xl backdrop-saturate-150 p-8 overflow-hidden shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_30px_60px_-24px_rgba(0,0,0,0.9)] hover:-translate-y-1.5 hover:border-primary-400/40 hover:bg-night-950/70 transition-all duration-300">
      {/* leve brilho de topo, dá volume ao vidro */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/[0.06] to-transparent" />
      <div className="absolute top-0 right-0 w-40 h-40 bg-primary-500/25 rounded-full blur-3xl opacity-0 group-hover:opacity-80 transition-opacity duration-500 -translate-y-1/2 translate-x-1/2" />
      <div className="relative">
        <div className="flex items-center justify-between mb-8">
          <span className="text-xs font-mono text-mist-500">0{index + 1}</span>
          <div className="h-11 w-11 rounded-xl bg-primary-400/10 border border-primary-400/20 flex items-center justify-center backdrop-blur-sm">
            <Icon className="text-primary-400" size={20} />
          </div>
        </div>
        <div className="flex items-baseline gap-1">
          <span
            ref={numRef}
            className="font-display text-6xl md:text-7xl font-bold text-mist-50 leading-none tracking-tightest [text-shadow:0_2px_30px_rgba(0,0,0,0.75)]"
          >
            0
          </span>
          <span className="font-display text-3xl text-primary-400 font-bold">{suffix}</span>
        </div>
        <div className="mt-4 text-mist-300 text-sm">{label}</div>
      </div>
    </div>
  );
};

/** Camada de fundo: foto + parallax + véus que garantem a legibilidade. */
const MetricsBackground = () => {
  const layerRef = useRef(null);
  const [hasPhoto, setHasPhoto] = useState(true);

  useEffect(() => {
    const el = layerRef.current;
    if (!el || !PARALLAX) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        el,
        { yPercent: -PARALLAX },
        {
          yPercent: PARALLAX,
          ease: 'none',
          scrollTrigger: {
            trigger: el.parentElement,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }, el);
    return () => ctx.revert();
  }, [hasPhoto]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* foto com parallax (container maior que a seção para nunca abrir buraco) */}
      {hasPhoto && (
        <div ref={layerRef} className="absolute -top-[14%] left-0 w-full h-[128%] will-change-transform">
          <img
            src={BG_SRC}
            alt=""
            /* NÃO usar loading="lazy" aqui: o container tem transform do GSAP
               e o Chrome acaba nunca disparando o request da imagem. */
            decoding="async"
            fetchpriority="low"
            onError={() => setHasPhoto(false)}
            className="w-full h-full object-cover"
            style={{
              opacity: BG_OPACITY,
              objectPosition: 'center top',
              transform: `translateY(${BG_SHIFT}) scale(${BG_ZOOM})`,
              transformOrigin: `center ${BG_ANCHOR}`,
              filter: `blur(${BG_BLUR}) brightness(${BG_BRIGHTNESS}) saturate(${BG_SATURATE})`,
            }}
          />
        </div>
      )}

      {/* 1. véu base leve — só assenta a foto, sem apagá-la */}
      <div className="absolute inset-0 bg-night-950/22" />

      {/* 2. fade vertical curto: cola a seção nas vizinhas sem escurecer o miolo */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,#050807_0%,rgba(5,8,7,0)_26%,rgba(5,8,7,0)_74%,#050807_100%)]" />

      {/* 3. proteção do texto: sólido à esquerda, foto livre à direita */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#050807_0%,rgba(5,8,7,0.82)_26%,rgba(5,8,7,0.28)_58%,rgba(5,8,7,0)_100%)]" />

      {/* 3b. vinheta radial: foca o olho no miolo e apaga as bordas quentes da foto */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_85%_at_42%_45%,rgba(5,8,7,0)_35%,rgba(5,8,7,0.55)_78%,rgba(5,8,7,0.9)_100%)]" />

      {/* 4. tom verde da marca, sem escurecer */}
      <div className="absolute inset-0 bg-gradient-to-tr from-primary-950/35 via-transparent to-primary-500/[0.07]" />

      {/* 5. brilho verde no canto — amarra com o resto do site */}
      <div className="absolute -bottom-40 -right-32 w-[42rem] h-[42rem] rounded-full bg-primary-500/[0.09] blur-[130px]" />

      {/* 6. grão: mata o banding do degradê e dá textura */}
      <div className="absolute inset-0 bg-noise opacity-[0.09] mix-blend-overlay" />

      {/* 7. linhas finas de acabamento */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </div>
  );
};

const Metrics = () => {
  const ref = useRevealOnScroll();
  const metrics = [
    { icon: Briefcase, value: '9', label: 'Projetos Entregues', suffix: '+' },
    { icon: Users, value: '40', label: 'Integrantes', suffix: '+' },
    { icon: TrendingUp, value: '7', label: 'Empresas Parceiras', suffix: '+' },
    { icon: Award, value: '2', label: 'Prêmios Conquistados', suffix: '' },
  ];

  return (
    <section ref={ref} className="relative py-28 bg-night-950 overflow-hidden isolate">
      <MetricsBackground />

      <div className="container-custom relative z-10">
        <div className="max-w-3xl mb-16">
          <span className="eyebrow reveal">Nosso impacto</span>
          <h2 className="section-title reveal mt-4 [text-shadow:0_2px_30px_rgba(5,8,7,0.9)]">
            Números que <span className="text-gradient">contam</span> uma história
          </h2>
          <p className="section-subtitle reveal mt-6 [text-shadow:0_1px_16px_rgba(5,8,7,0.9)]">
            Demonstrações concretas da nossa dedicação ao agronegócio brasileiro.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => (
            <MetricCard key={index} {...metric} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Metrics;
